import React, { useCallback } from 'react';
import { NodeProps } from 'reactflow';
import type { PhaseStackGroupData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { useChatDagRuntime } from '../../../../contexts/ChatDagRuntimeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';

// Per-turn "phase stack" bounding box wrapping all rounds + tools +
// peers/chips between the user prompt and the synthesized answer.
// Sized by the layout post-pass from the union of child positions +
// padding. Renders BEHIND the stack's contents as a rounded rect.
//
// The entire backdrop is a click target — clicking anywhere inside
// the stack's bounds (that isn't already covered by a tool/peer/
// round node) selects that stack and scrolls the chat to the turn's
// bubble. Child nodes still paint on top (DOM order) so their own
// click handlers fire first on their footprint; the backdrop catches
// the gaps and margins.
export const PhaseStackGroupNode: React.FC<NodeProps<PhaseStackGroupData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const { viewportTurnId, inFlightTurnId, errorTurnId } = useChatDagRuntime();
  const { focusBubble } = usePersonaChatUI();

  const handleStackClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (data.turnKey) focusBubble(data.turnKey);
    },
    [focusBubble, data.turnKey],
  );

  const isInFlight = inFlightTurnId != null && inFlightTurnId === data.turnKey;
  const isError = errorTurnId != null && errorTurnId === data.turnKey;
  const isViewport = viewportTurnId != null && viewportTurnId === data.turnKey;

  let borderClass: string;
  let fillClass: string;
  if (isError) {
    borderClass = darkMode ? 'border-rose-500/70' : 'border-rose-500/80';
    fillClass = darkMode ? 'bg-rose-950/20' : 'bg-rose-50/40';
  } else if (isInFlight) {
    borderClass = darkMode ? 'border-emerald-400/75' : 'border-emerald-500/85';
    fillClass = darkMode ? 'bg-emerald-950/25' : 'bg-emerald-50/40';
  } else if (isViewport) {
    borderClass = darkMode ? 'border-emerald-600/50' : 'border-emerald-500/60';
    fillClass = darkMode ? 'bg-emerald-950/12' : 'bg-emerald-50/25';
  } else {
    borderClass = darkMode ? 'border-gray-600/40' : 'border-gray-400/45';
    fillClass = darkMode ? 'bg-gray-800/20' : 'bg-white/50';
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleStackClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStackClick(e as unknown as React.MouseEvent);
        }
      }}
      title={`Stack ${data.turnIndex + 1}: ${data.name} — click to jump chat to this phase`}
      className={`group/stack w-full h-full rounded-2xl border-2 ${borderClass} ${fillClass} transition-colors duration-300 cursor-pointer relative hover:brightness-110`}
      style={{
        boxShadow:
          isInFlight || isError
            ? darkMode
              ? 'inset 0 0 22px rgba(16, 185, 129, 0.08)'
              : 'inset 0 0 22px rgba(5, 150, 105, 0.06)'
            : 'none',
      }}
    >
      {/* Name + counts in the top-left corner. Auto-derived name lets
          the controls panel list stacks by human-readable ident. The
          label is display-only — the click handler lives on the whole
          backdrop so clicking anywhere in the stack's bounds fires
          the focusBubble jump. */}
      <div
        className={`absolute top-1.5 left-3 text-[10px] font-mono tracking-tight ${
          darkMode ? 'text-gray-400 group-hover/stack:text-gray-200' : 'text-gray-500 group-hover/stack:text-gray-800'
        } opacity-80 group-hover/stack:opacity-100 select-none max-w-[85%] truncate transition-opacity`}
      >
        <span className="uppercase opacity-60">
          Stack {data.turnIndex + 1}
        </span>
        <span className="mx-1 opacity-40">·</span>
        <span className="opacity-90">{data.name}</span>
        {isInFlight ? (
          <span className="ml-1.5 text-emerald-400">⏳ running</span>
        ) : null}
        {isError ? (
          <span className="ml-1.5 text-rose-400">⚠ error</span>
        ) : null}
      </div>
      {/* Round / tool count in the top-right corner. */}
      <div
        className={`absolute top-1.5 right-3 text-[9px] font-mono uppercase tracking-widest ${
          darkMode ? 'text-gray-500' : 'text-gray-400'
        } opacity-60 select-none`}
      >
        {data.roundCount} {data.roundCount === 1 ? 'phase' : 'phases'}
        {data.toolCount > 0 ? ` · ${data.toolCount} tools` : ''}
      </div>
    </div>
  );
};

export default PhaseStackGroupNode;
